// FindingFilterBar.js — Filter/sort/search/viewMode bar for Deep Security Tab
import React, { useState, useCallback } from 'react';
import { track as trackTelemetry } from '../../services/telemetryService';
import './FindingFilterBar.css';

const SEVERITY_LEVELS = ['critical', 'high', 'medium', 'low', 'info'];
const SEVERITY_LABELS = { critical: 'Crit', high: 'High', medium: 'Med', low: 'Low', info: 'Info' };

const FindingFilterBar = ({
    summary,
    totalFindings,
    filteredCount,
    activeFilters,
    onToggleFilter,
    onShowAll,
    sortBy,
    onSortChange,
    searchQuery,
    onSearchChange,
    viewMode,
    onViewModeChange,
    allExpanded,
    onExpandAll,
    onCollapseAll
}) => {
    const [searchInput, setSearchInput] = useState(searchQuery || '');

    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchInput(value);
        clearTimeout(window._dsSearchTimeout);
        window._dsSearchTimeout = setTimeout(() => {
            onSearchChange(value);
            // PRD-151 WP7: fire on the DEBOUNCED change (not every keystroke), skip
            // empty. PRIVACY: ship ONLY {length, truncatedTerm(≤3 chars)} — NEVER the
            // full query string.
            const term = value.trim();
            if (term) {
                try {
                    trackTelemetry('search', {
                        meta: { length: term.length, truncatedTerm: term.slice(0, 3) },
                    });
                } catch (err) { /* best-effort */ }
            }
        }, 300);
    }, [onSearchChange]);

    const handleClearSearch = () => {
        setSearchInput('');
        onSearchChange('');
    };

    const allActive = SEVERITY_LEVELS.every(s => activeFilters.has(s));

    return (
        <div className="ds-filter-bar">
            {/* Interactive Summary Bar */}
            <div className="ds-summary-bar">
                <span className="ds-summary-title">
                    Deep Security Scan — {totalFindings} finding{totalFindings !== 1 ? 's' : ''}
                </span>
                <div className="ds-severity-toggles">
                    {SEVERITY_LEVELS.map(sev => {
                        const count = summary[sev] || 0;
                        const isActive = activeFilters.has(sev);
                        return (
                            <button
                                key={sev}
                                className={`ds-severity-toggle ds-sev-${sev} ${isActive ? 'active' : 'dimmed'}`}
                                onClick={() => onToggleFilter(sev)}
                                title={`${isActive ? 'Hide' : 'Show'} ${sev} findings`}
                            >
                                <span className="ds-sev-count">{count}</span>
                                <span className="ds-sev-label">{SEVERITY_LABELS[sev]}</span>
                            </button>
                        );
                    })}
                </div>
                {!allActive && (
                    <button className="ds-show-all-btn" onClick={onShowAll}>Show All</button>
                )}
            </div>

            {/* Controls Row */}
            <div className="ds-controls-row">
                {/* Search */}
                <div className="ds-search-wrapper">
                    <input
                        type="text"
                        className="ds-search-input"
                        placeholder="Search findings..."
                        value={searchInput}
                        onChange={handleSearchChange}
                    />
                    {searchInput && (
                        <button className="ds-search-clear" onClick={handleClearSearch}>&times;</button>
                    )}
                </div>

                {/* Sort */}
                <select
                    className="ds-sort-select"
                    value={sortBy}
                    onChange={(e) => onSortChange(e.target.value)}
                >
                    <option value="severity">Sort: Severity</option>
                    <option value="host">Sort: Host</option>
                    <option value="template">Sort: Template</option>
                    <option value="name">Sort: Name</option>
                </select>

                {/* View Mode Toggle */}
                <div className="ds-view-toggle">
                    <button
                        className={`ds-view-btn ${viewMode === 'tool' ? 'active' : ''}`}
                        onClick={() => onViewModeChange('tool')}
                    >By Tool</button>
                    <button
                        className={`ds-view-btn ${viewMode === 'severity' ? 'active' : ''}`}
                        onClick={() => onViewModeChange('severity')}
                    >By Severity</button>
                </div>

                {/* Expand/Collapse All */}
                <div className="ds-expand-controls">
                    <button
                        className={`ds-expand-btn ${allExpanded === true ? 'active' : ''}`}
                        onClick={onExpandAll}
                        title="Expand all findings"
                    >Expand All</button>
                    <button
                        className={`ds-expand-btn ${allExpanded === false ? 'active' : ''}`}
                        onClick={onCollapseAll}
                        title="Collapse all findings"
                    >Collapse All</button>
                </div>
            </div>

            {/* Filter Status */}
            {filteredCount < totalFindings && (
                <div className="ds-filter-status">
                    Showing {filteredCount} of {totalFindings} findings
                    {searchInput && <span> matching "{searchInput}"</span>}
                </div>
            )}
        </div>
    );
};

export default FindingFilterBar;
